//#region Angular Imports

import { StateService } from "@uirouter/angularjs"

//#endregion

//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import dxButton from "devextreme/ui/button"

//#endregion

//#region Helper Imports

import { CommonHelper, FilterNames } from '../../helpers/common-helper'
import { EventsNames } from '../../helpers/events-names'
import { LocalizationHelper } from '../../helpers/localization-helper'
import { LogHelper, ToastHelper, TypeHelper, TemplateHelper, ConfigHelper } from '../../helpers'
import { MapHelper } from '../../helpers/map-helper'
import { PopupType } from '../../helpers/popup-manager'

//#endregion

//#region Model Imports

import { Activity } from '../../models/common/activity'
import { ColumnInfo } from '../../models/custom';
import { Enums } from '../../models/common/enums'
import { GetViewColumnOptions } from '../../models/core/op-view'
import { IOpObject } from '../../models/util/interfaces'
import { OpBounds } from '../../models/custom/op-map-area'
import { OpDataType, OpIssue, OpLocation, OpTask, OpView, OpViewColumn } from '../../models/core'
import { OpExportPluginParam } from '../../models/util';

//#endregion

//#region Service Imports

import { GridService, GridContextMenuService } from '../../services/grid'
import { LocationService, LocationRelatedObjectService } from '../../services/location'
import { DetailsOptions, SystemService } from '../../services/system'
import { UserSessionService } from '../../services/user-session'

//#endregion

// #region Imports

import { ColumnModel } from '../api/api.models'
import { BaseGridGridsterController, ScopeBaseGridsterGrid } from '../common/common.base.grid.gridster.controller'
import { CreateRelatedItemsDataSource, CreateRelatedItemsOptions, ICreateRelatedItems, TargetItemFactoryOptions } from '../common/create-related-items/create-related-items.interfaces'
import { ExportOptions } from '../common/export'
import { GridDetailsController } from "../common/grid-details/grid-details.directive.controller"
import { ButtonsOptions, GridController, GridOptions, GridTypes } from '../common/grid/grid.controller'
import { ImportOptions } from '../common/import'
import { MapLegendItem, MapOptions } from '../common/map2/map.directive.controller'
import { Api, LocalDictionaryFactory } from './../api/api.module'
import { ILocationsMassGroupsEdit, LocationsMassGroupsEditOptions } from './locations-mass-groups-edit/locations-mass-groups-edit.interfaces'
import { ILocationsMassSendConfigurationProfile, LocationsMassSendConfigurationProfileOptions } from './locations-mass-send-configuration-profile/locations-mass-send-configuration-profile.interfaces'
import { GridCustomButtonsOptions, GridCustomButtonOptions, GridCustomButtonLocation } from "../common/grid"
import { IIMRLocationSchemaAssignPopup, IIMRLocationSchemaAssignPopupInitializedArgs, IMRILocationSchemaAssignPopupOptions } from "./location-schema-assign-popup/location-schema-assign-popup.interfaces"

// #endregion

// #region ILocationsScope

export interface ILocationsScope
    extends ScopeBaseGridsterGrid {
    NoDefinedContentText: string
    ShowGridText: string

    mainGridOptions: GridOptions
    massSendConfigurationProfileOptions: LocationsMassSendConfigurationProfileOptions
    massLocationGroupsEditOptions: LocationsMassGroupsEditOptions

    createTasksOptions: CreateRelatedItemsOptions<OpLocation, OpTask>
    createIssuesOptions: CreateRelatedItemsOptions<OpLocation, OpIssue>

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }

    locationSchemaAssignPopupOptions: IMRILocationSchemaAssignPopupOptions
}

// #endregion

export class LocationsController
    extends BaseGridGridsterController {
    // #region Members

    private locationsMassGroupsEdit: ILocationsMassGroupsEdit

    private massSendConfigurationProfile: ILocationsMassSendConfigurationProfile

    private grid: GridController<OpLocation>
    private copyGridButton: dxButton

    private createTasks: ICreateRelatedItems<OpLocation, OpTask>
    private createIssues: ICreateRelatedItems<OpLocation, OpIssue>

    private locationSchemaAssignPopup: IIMRLocationSchemaAssignPopup

    // #endregion

    // #region Ctor

    constructor(
        protected $scope: ILocationsScope,
        protected $state: StateService,
        $timeout: ng.ITimeoutService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        protected locationService: LocationService,
        protected locationRelatedObjectService: LocationRelatedObjectService,
        private gridService: GridService,
        private gridContextMenuService: GridContextMenuService,
        protected userSession: UserSessionService,
        protected systemService: SystemService,
        private moduleName: Enums.SimaxModuleName,
        referenceType: Enums.ReferenceType
    ) {
        super(
            $scope,
            $timeout,
            $state,
            userSession,
            systemService,
            api,
            localDictionary,
            referenceType,
            GridDetailsController.getOptionsByReferenceType(referenceType),
            Enums.SimaxModuleName[moduleName],
            referenceType,
            true
        )

        this.setTexts()
        this.setConfirmationsOptions()
        this.setEvents()
        this.setMainGridOptions()
        this.setMassSendConfigurationProfileOptions()
        this.setMassGroupsEditOptions()
        this.setCreateIssuesOptions()
        this.setCreateTasksOptions()
        this.setLocationSchemaAssignPopupOptions()
    }

    // #endregion

    // #region Events

    private onInitialized(args: any, grid: GridController<OpLocation>): void {
        this.dataGrid = args.component
        this.grid = grid

        this.setGridId(args.tableId)
        this.onGridInit(args.tableId)
    }

    private onAdd(idTemplateLocation: number): void {
        this.locationService.openCreate(idTemplateLocation)
    }

    private onCopy(): void {
        if (this.grid.selectedCount === 1) {
            this.locationService.openCopy(this.grid.selectedItemId, this.grid.selectedItem.idDistributor)
        }
    }

    private onEdit(): void {
        if (this.grid.selectedCount === 1) {
            this.locationService.openEdit(this.grid.selectedItemId, this.grid.selectedItem.idDistributor)
        } else {
            const columnCount: number = this.grid.dxGrid.columnCount()
            const columns: any[] = []

            for (let i = 0; i < columnCount; ++i) {
                columns.push(this.grid.dxGrid.columnOption(i));
            }

            let groupReferenceValue: number = null
            const selectedItems: any[] = this.grid.selectedGridItems
            if (_.size(selectedItems) > 0) {
                const location: OpLocation = this.gridService.getSourceObjectFromGridObject<OpLocation>(_.first(selectedItems), LocationService.REFERENCE_TYPE)

                if (location != null) {
                    groupReferenceValue = location.dataList.getValue(Enums.DataType.HELPER_ID_DISTRIBUTOR)
                }

                if (_.size(selectedItems) > 1) {
                    const hasDifferentDistributors: boolean = selectedItems.some((gridObj: any) => {
                        const loc: OpLocation = this.gridService.getSourceObjectFromGridObject<OpLocation>(gridObj, LocationService.REFERENCE_TYPE)

                        if (location != null) {
                            const idDistributor: number = location.dataList.getValue(Enums.DataType.HELPER_ID_DISTRIBUTOR)
                            if (groupReferenceValue != idDistributor) {
                                return true
                            }
                        }

                        return false
                    })

                    if (hasDifferentDistributors) {
                        groupReferenceValue = null
                    }
                }
            }

            this.$scope.$root.$broadcast(
                EventsNames.INITIALIZE_BULK_EDIT_POPUP,
                this.locationService.getCreateEditBulkOptions(
                    columns,
                    groupReferenceValue,
                    this.grid.selectedGridItems
                )
            )
        }
    }

    private onShowDetails(args: any): void {
        const detailsOptions: DetailsOptions = this.locationService.getDetailsOptions(this.grid.selectedItemId, this.grid.pageItemsIds)

        this.showDetails(args, detailsOptions)
    }

    private onDelete(args: any): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onConfirmDelete(): void {
        this.locationService
            .deleteLocation(this.grid.selectedItemId)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
            .fail((error: any) => {
                LogHelper.error(LocationService.LOG_PREFIX, error)
            })
    }

    private onMassGroupingLocationAssign(args: any): void {
        const gridData: any[] = _.size(args) > 0 && _.size(args[0]) > 0 && Array.isArray(args[0])
            ? args[0]
            : TypeHelper.clone(this.dataGrid.getSelectedRowsData())

        if (_.size(gridData) > 0) {
            const locations: OpLocation[] = gridData.map((gridRow: any) => this.gridService.getSourceObjectFromGridObject(gridRow, this.referenceType) as OpLocation)

            let containsMissingIdDistributor: boolean = false

            locations.forEach((location: OpLocation) => {
                if (
                    location.idDistributor == null
                    || location.idDistributor == 0
                ) {
                    const dataIdDistributor: number = location.dataList != null && location.dataList.length > 0
                        ? location.dataList.getValue(Enums.DataType.HELPER_ID_DISTRIBUTOR, 0, null)
                        : null

                    if (dataIdDistributor != null) {
                        location.idDistributor = dataIdDistributor
                    } else {
                        containsMissingIdDistributor = true
                    }
                }
            })

            if (containsMissingIdDistributor) {
                ToastHelper.showError(LocalizationHelper.Text.FailedToOpenMassGroupingLocationAssign);
                LogHelper.warn(LocationService.LOG_PREFIX, 'Locations are missing IdDistributor and failed to retreive its value from other sources')
                return;
            }

            const hasDifferentDistributors: boolean = _.size(locations) > 1
                && locations.some((item: OpLocation) => item.idDistributor !== locations[0].idDistributor)

            if (hasDifferentDistributors) {
                ToastHelper.showWarning(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditIsEnableWithinOneDistributor))
            } else if (this.locationsMassGroupsEdit != null) {
                this.locationsMassGroupsEdit.setLocations(locations)
                this.locationsMassGroupsEdit.show()
            } else {
                LogHelper.warn(LocationService.LOG_PREFIX, 'Tried to open LocationsMassGroupEdit but component has not been initialized')
            }

        } else {
            ToastHelper.showWarning(LocalizationHelper.Text.YouHaveToSelectAtleastOneLocation);
            LogHelper.warn(LocationService.LOG_PREFIX, 'Tried to open LocationsMassGroupEdit but no rows were selected')
        }
    }

    private onLocationSchemaAssign(): void {
        if(this.locationSchemaAssignPopup != null){
            this.locationSchemaAssignPopup.showPopup(this.grid.selectedItemId)
        }
    }

    private onMassGroupsEditInitialized(component: ILocationsMassGroupsEdit): void {
        this.locationsMassGroupsEdit = component
    }

    private onCreateRelatedItems(args: any[]): void {
        if (_.size(args) >= 3) {
            const popupType: string = args[0]
            const gridData: any[] = args[1]
            const columns: any[] = args[2]

            const locations: OpLocation[] = _.size(gridData) > 0
                ? gridData.map((gridRow: any) => this.gridService.getSourceObjectFromGridObject(gridRow, this.referenceType) as OpLocation)
                : []

            const dataSource: CreateRelatedItemsDataSource<OpLocation> = {
                sourceItems: locations,
                dxColumns: columns
            }

            let controller: ICreateRelatedItems<OpLocation, IOpObject> = null

            switch (popupType) {
                case PopupType.TASK_POPUP: {
                    controller = this.createTasks
                    break;
                }
                case PopupType.ISSUE_POPUP: {
                    controller = this.createIssues
                    break;
                }
                default: {
                    ToastHelper.showError(LocalizationHelper.Text.AttemptedToOpenCreateRelatedItemsFormButProvidedPopupTypeIsNotSupported)
                    LogHelper.warn(LocationService.LOG_PREFIX, `Attempted to open 'CreateRelatedItems' but provided popup type is not supported: '${popupType}'.`)
                    break;
                }
            }

            if (controller == null) {
                ToastHelper.showError(LocalizationHelper.Text.FailedToOpenCreateRelatedItemsForm)
                LogHelper.warn(LocationService.LOG_PREFIX, 'Attempted to open `CreateRelatedItems` but selected controller is null.')
            } else {
                controller.setDataSource(dataSource)
                controller.show()
            }
        } else {
            ToastHelper.showError(LocalizationHelper.Text.FailedToOpenCreateRelatedItemsForm)
            LogHelper.warn(LocationService.LOG_PREFIX, 'Attempted to open `CreateRelatedItems` but no arguments provided.')
        }
    }

    // #endregion

    //#region Copy

    private toggleCopyButton(): void {
        if (this.copyGridButton != null && this.grid != null) {
            this.copyGridButton.option('disabled', this.grid.selectedCount !== 1)
        }
    }

    //#endregion


    private getGridCustomButtonOptions(): GridCustomButtonsOptions {
        const customButtons: GridCustomButtonOptions[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_COPY)) {
            customButtons.push({
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.CONTENT_COPY, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Copy),

                    onClick: (e: any) => {
                        this.onCopy()
                    },

                    onInitialized: (e: any) => {
                        this.copyGridButton = e.component

                        this.toggleCopyButton()
                    }
                }
            })
        }

        return {
            buttons: customButtons
        }
    }

    // #region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdLocation, {
            idReferenceTypes: this.grid.selectedItemsIds,

            onEdit: () => this.onEdit(),
            onCopy: () => this.onCopy(),
            getCorrespondingActionType: (idReferenceType: Enums.ReferenceType, args: any) => this.getCorrespondingActionType(idReferenceType, args),
            getLocationsProblems: () => this.getLocationsProblems(),
            planDelivery: () => this.planDelivery(),
            onMassGroupingLocationAssign: (args: any) => this.onMassGroupingLocationAssign(args),
            onLocationSchemaAssign: () => this.onLocationSchemaAssign(),
        })

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_LIST) ? "['" + Activity[Activity.LOCATION_LIST] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_DEL) ? "['" + Activity[Activity.LOCATION_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_EDIT) ? "['" + Activity[Activity.LOCATION_EDIT] + "']" : null,
            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_ADD) ? "['" + Activity[Activity.LOCATION_ADD] + "']" : null,
            importPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_IMPORT_OP) ? "['" + Activity[Activity.LOCATION_IMPORT_OP] + "']" : null,
            exportPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_EXPORT_OP) ? "['" + Activity[Activity.LOCATION_EXPORT_OP] + "']" : null,
            taskMultiplePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_MULTIPLE_ADD) ? "['" + Activity[Activity.TASK_MULTIPLE_ADD] + "']" : null,
            issueMultiplePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ISSUE_MULTIPLE_ADD) ? "['" + Activity[Activity.ISSUE_MULTIPLE_ADD] + "']" : null,
            locationConfigurationMultiplePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_CONFIGURATION_SEND) ? "['" + Activity[Activity.LOCATION_CONFIGURATION_SEND] + "']" : null,
            assigningToGroupPermisions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_GROUP_MULTIPLE_ASSIGN_OP) ? "['" + Activity[Activity.LOCATION_GROUP_MULTIPLE_ASSIGN_OP] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            problemConfirmationPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_PROBLEM_CONFIRMATION_OP) ? "['" + Activity[Activity.LOCATION_PROBLEM_CONFIRMATION_OP] + "']" : null,
            planDeliveryPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.LOCATION_PLAN_DELIVERY_TIME_OP) ? "['" + Activity[Activity.LOCATION_PLAN_DELIVERY_TIME_OP] + "']" : null,

            assigningToGroupMultiple: true,
            problemConfirmation: true,
            planDelivery: true,
            addLocationConfigurationMultiple: true,
            addIssueMultiple: true,
            addTaskMultiple: true,

            onShowClick: (args: any) => { this.onShowDetails(args) },
            onAddClick: (template: number) => this.onAdd(template),
            onEditClick: (args: any) => this.onEdit(),
            onDeleteClick: (args: any) => this.onDelete(args),
            onShowMap: () => { return this.showMapDetails() },
            onShowPreview: () => { return this.showPreviewDetails() },

            onOpenMassAssignedConfigurationProfile: (popupType: string, gridData: any, columns: any) => { this.showMassSendConfigurationLocation(popupType, gridData, columns) },
            onProblemConfirmationClick: () => { this.getLocationsProblems() },
            onPlanDeliveryClick: () => { this.planDelivery() }
        }
    }

    private getExportOptions(): ExportOptions {
        return {
            customExportMethod: (referenceType: Enums.ReferenceType, columns: ColumnInfo[], customWhereClause: string, dllName: string, params: OpExportPluginParam[]) => this.userSession.getDataProvider().Location.locationExport(referenceType, columns, customWhereClause, dllName, params),
            referenceType: this.referenceType,
            pluginOptions: {
                moduleName: this.moduleName,
                popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EXPORT, this.moduleName)
            }
        }
    }

    private getImportOptions(): ImportOptions {
        return {
            dictionaryMethodName: null,
            popupName: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ImportLocations),
            requestMethodType: null,
            referenceType: this.referenceType,
            moduleName: this.moduleName,
            columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.LocationModuleMainGrid)
        }
    }

    private getPatternsProviderOptions(view: OpView): {
        patternsProvider: () => JQueryPromise<OpLocation[]>,
        displayField: string
    } {
        const options: GetViewColumnOptions = new GetViewColumnOptions(Enums.DataType.LOCATION_NAME)
        options.keyColumn = view.getPrimaryKeyColumn()

        const locationNameColumn: OpViewColumn = view.getViewColumn(options)

        return {
            patternsProvider: () => { return this.locationService.getLocationPatterns() },
            displayField: locationNameColumn.name
        }
    }

    private getCreateRelatedItemsOptions<TTarget>(
        popupTitle: string,
        moduleName: Enums.SimaxModuleName,
        targetReferenceType: Enums.ReferenceType,
        targetFactory: (factoryOptions: TargetItemFactoryOptions<OpLocation>) => TTarget,
        saveDelegate: (toSave: TTarget) => JQueryPromise<TTarget>,
        initializeDelegate: (component: ICreateRelatedItems<OpLocation, TTarget>) => void
    ): CreateRelatedItemsOptions<OpLocation, TTarget> {
        return {
            popupTitle: popupTitle,

            sourceOptions: {
                keyProvider: (obj: OpLocation) => { return obj.idLocation },

                moduleName: this.moduleName,
                referenceType: this.referenceType
            },

            targetOptions: {
                columns: {
                    groupCategory: Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_EDIT,
                    groupModuleName: moduleName,

                    // Nie chcemy pozowli� na to by w przypadku dodawania task�w/issue na podstawie lokalizacji
                    // by�a mo�liwo�� zmiany dystrybutora lub lokalizacji bo jest to bez sensu. W takiej sytuacji lepiej
                    // po prostu stworzy� taska.
                    // Dlatego m�wimy kontrolce, �e ma tych datatype nie pokazywa� nizale�nie od konfiguracji grup.
                    hiddenDataTypes: [
                        new OpDataType({ ID_DATA_TYPE: Enums.DataType.HELPER_ID_LOCATION }),
                        new OpDataType({ ID_DATA_TYPE: Enums.DataType.HELPER_ID_DISTRIBUTOR }),
                    ]
                },

                requiredColumns: {
                    groupCategory: Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PARAMS_REQUIRED,
                    groupModuleName: moduleName
                },

                referenceType: targetReferenceType,

                itemFactory: targetFactory,
                saveDelegate: saveDelegate
            },

            gridOptions: {
                referenceType: this.referenceType,

                allowColumnReordering: !this.userSession
                    .getLoggedOperator()
                    .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.LocationModuleMainGrid),

                dataSourceParser: this.locationService.getMainGridDataSourceParser()
            },

            onInitialize: initializeDelegate
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => { this.onDeleted() }
        )

        this.$scope.$on(
            EventsNames.OPEN_MASS_GROUPS_EDIT_POPUP,
            (event: ng.IAngularEvent, ...args: any[]) => { this.onMassGroupingLocationAssign(args) }
        )

        this.$scope.$on(
            EventsNames.OPEN_LOCATION_CREATE_ISSUES_TASKS_POPUP,
            (event: ng.IAngularEvent, ...args: any[]) => { this.onCreateRelatedItems(args) }
        )

        this.$scope.showDetails = (args: any) => this.onShowDetails(args)

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__REFRESH, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => { this.grid.dxGrid.refresh() }
        )
    }

    private setMassGroupsEditOptions(): void {
        this.$scope.massLocationGroupsEditOptions = {
            referenceType: this.referenceType,
            moduleName: this.moduleName,
            onInitialized: (component: ILocationsMassGroupsEdit) => { this.onMassGroupsEditInitialized(component) },
            onSaved: () => { this.grid.dxGrid.refresh() }
        }
    }

    private setMainGridOptions(): void {
        $.when<any>(
            this.locationService
                .getMainGridColumns(),
            this.userSession
                .getDataProvider()
                .View
                .getActiveView(LocationService.REFERENCE_TYPE)
        )
            .then((columnsArray: ColumnModel[][], view: OpView) => {
                const [columns, defaultColumns]: ColumnModel[][] = columnsArray

                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    customButtonOptions: this.getGridCustomButtonOptions(),
                    dataSourceParser: this.locationService.getMainGridDataSourceParser(),
                    defaultColumnsDictionary: defaultColumns,
                    exportOptions: this.getExportOptions(),
                    getDataSourceMethod: this.userSession.getDataProvider().Location.getLocationFilter,
                    importOptions: this.getImportOptions(),
                    idReferenceType: this.referenceType,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.locationSaved,
                    name: LocationService.GRID_NAME,
                    patternsProviderOptions: this.getPatternsProviderOptions(view),
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.LocationModuleMainGrid),

                    contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
                    onDataReady: (data: any) => { this.onDataReady(data) },
                    onInitialized: (args: any, grid: GridController<OpLocation>) => { this.onInitialized(args, grid) },
                    onRowDoubleClick: (args: any) => { this.onShowDetails(args) },
                    onSelectionChanged: (args: any) => {
                        this.toggleCopyButton()
                        this.onSelectionChanged(args)
                    },
                    onUpdateGridsterPosition: (args: any) => { this.setGridsterPosition(args) }
                }
            })
    }

    private setMassSendConfigurationProfileOptions(): void {
        this.$scope.massSendConfigurationProfileOptions = {
            onInitialized: (component: ILocationsMassSendConfigurationProfile) => {
                this.massSendConfigurationProfile = component
            }
        }
    }

    private setCreateTasksOptions(): void {
        this.$scope.createTasksOptions = this.getCreateRelatedItemsOptions(
            LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MassTaskCreate),
            Enums.SimaxModuleName.Task,
            Enums.ReferenceType.IdTask,
            (factoryOptions: TargetItemFactoryOptions<OpLocation>) => { return this.locationRelatedObjectService.createTaskFromLocation(factoryOptions) },
            (task: OpTask) => { return this.userSession.getDataProvider().Task.saveTask(task) },
            (component: ICreateRelatedItems<OpLocation, OpTask>) => { this.createTasks = component }
        )
    }

    private setCreateIssuesOptions(): void {
        this.$scope.createIssuesOptions = this.getCreateRelatedItemsOptions(
            LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MassIssueCreate),
            Enums.SimaxModuleName.Issue,
            Enums.ReferenceType.IdIssue,
            (factoryOptions: TargetItemFactoryOptions<OpLocation>) => { return this.locationRelatedObjectService.createIssueFromLocation(factoryOptions) },
            (issue: OpIssue) => { return this.userSession.getDataProvider().Issue.saveIssue(issue) },
            (component: ICreateRelatedItems<OpLocation, OpIssue>) => { this.createIssues = component }
        )
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setTexts(): void {
        this.$scope.NoDefinedContentText = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDefinedContent)
        this.$scope.ShowGridText = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ShowGrid)
    }

    private setLocationSchemaAssignPopupOptions():void{
        this.$scope.locationSchemaAssignPopupOptions = {
            onInitialized:(e: IIMRLocationSchemaAssignPopupInitializedArgs)=>{
                this.locationSchemaAssignPopup = e.component
            }
        }
    }

    // #endregion

    // #region LocationsController

    private showMassSendConfigurationLocation(popupType: string, gridData: any, columns: any): void {
        this.massSendConfigurationProfile.show({
            popupType: popupType,
            gridData: gridData,
            columns: columns
        })
    }

    // #endregion

    // #region BaseGridGridsterController Implementation

    protected onGridsterMapConfiguration(mapOptions: MapOptions): void {
        mapOptions.enableAreaSelect = true

        mapOptions.onAreaSelect = (bounds: OpBounds) => {
            MapHelper
                .getCoordColumnNames(this.userSession.getDataProvider())
                .then(([latColName, lonColName]: [string, string]) => {
                    this.addCustomGridFilter(CommonHelper.getLonLatFilter(bounds, latColName, lonColName, FilterNames.LocationsByMarkers))
                })
        }

        MapHelper
            .getLegendItems(this.userSession.getDataProvider(), Enums.ReferenceType.IdLocation)
            .then((items: MapLegendItem[]) => {
                mapOptions.legendItems = items
            })
    }

    // #endregion
}
